<script lang="ts">
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import SettingsCard from '$lib/components/map/mapTools/settings/settingsCard/SettingsCard.svelte';
	import PoiCategories from '$lib/components/pois/filters/PoiCategories.svelte';
	import DetailedViewSwitch from '$lib/components/pois/list/DetailedViewSwitch.svelte';
	import { type MainPoiCategory, mainPoiCategoryList } from '$lib/components/pois/types';
	import { isMainPoiCategory } from '$lib/components/pois/utils';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getPoiCategoryToggleTrackingEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import * as m from '$paraglide/messages';

	interface Props {
		selectedPoiCategories: MainPoiCategory[];
		detailedView: boolean | undefined;
		showPoiCategorySelector?: boolean;
		withPartner?: boolean;
		onPoiCategoriesChange?: (categories: MainPoiCategory[]) => void;
	}

	const {
		selectedPoiCategories,
		detailedView,
		showPoiCategorySelector = true,
		withPartner = false,
		onPoiCategoriesChange,
	}: Props = $props();

	function unselectAllPoiCategories() {
		track({
			action: 'toggle_all_pois',
			payload: {
				action: 'hide',
				context: 'modal',
			},
		});
		onPoiCategoriesChange?.([]);
	}

	function selectAllPoiCategories() {
		track({
			action: 'toggle_all_pois',
			payload: {
				action: 'show',
				context: 'modal',
			},
		});
		onPoiCategoriesChange?.(mainPoiCategoryList);
	}

	const arePoiCategoriesSelected = $derived(selectedPoiCategories.length > 0);
</script>

<SettingsCard>
	{#snippet title()}
		<div>{m.poi_category_settings__header()}</div>
		{#if showPoiCategorySelector}
			<Button
				size="sm"
				variant="ghost"
				text={arePoiCategoriesSelected
					? m.poi_category_settings__unselect_all()
					: m.poi_category_settings__select_all()}
				icon={arePoiCategoriesSelected ? 'visibility-off' : 'visibility'}
				iconPosition="right"
				onclick={arePoiCategoriesSelected ? unselectAllPoiCategories : selectAllPoiCategories}
			/>
		{/if}
	{/snippet}
	{#if showPoiCategorySelector}
		<div class="pb-2">
			<DetailedViewSwitch {detailedView} />
		</div>
		<PoiCategories
			{selectedPoiCategories}
			{withPartner}
			withLabel
			onChange={(categories, info) => {
				onPoiCategoriesChange?.(categories.filter(isMainPoiCategory));
				track(getPoiCategoryToggleTrackingEvent({ ...info, updatedSelectedCategories: categories, context: 'modal' }));
			}}
		/>
	{:else}
		<div class="bg-neutral-weak p-4 rd-1 border-1 border-solid border-neutral-soft flex gap-2">
			<Icon icon="info" class="text-md" />
			<div class="flex-1 text-sm">
				{m.poi_category_settings__not_available()}
			</div>
		</div>
	{/if}
</SettingsCard>
